import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UseChronologyDownloadProps {
  chronologyId: string;
  matterId: string;
  subsetId: string;
}

interface ExportResponse {
  exportId: string;
  jobId: string;
}

interface DownloadUrlResponse {
  downloadUrl: string;
}

const useChronologyDownload = ({ chronologyId, matterId, subsetId }: UseChronologyDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadChronology = async ({
    styleFormat,
    title,
    columns,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments = false,
    orderByDate,
  }: {
    styleFormat: StyleFormat;
    downloadFormat: DownloadFormat;
    title: string;
    columns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }): Promise<ExportResponse> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        title,
        columns,
        style,
        documentOptions,
        orderByDate,
      },
    });

    const endpoint = includeSourceDocuments
      ? `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/download-with-documents`
      : `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/download`;

    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(errorBody?.message ?? 'Failed to start chronology export');
    }

    return await response.json();
  };

  const downloadExport = async (title: string, exportId: string, format?: DownloadFormat): Promise<void> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.GET,
    });

    const formatParam = format ? `&format=${format}` : '';
    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/chronology/${chronologyId}/export-download-url?exportId=${exportId}${formatParam}`;
    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      throw new Error('Failed to get chronology export download URL');
    }

    const data: DownloadUrlResponse = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = format ? `${title}.${format}` : `${title} with source documents.zip`;
    link.click();
  };

  return {
    downloadMutation: useMutation(downloadChronology, {
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to download chronology. Please try again.');
      },
    }),
    downloadExport,
  };
};

export default useChronologyDownload;
